"use client";

import { useCallback, useRef } from "react";
import { preload } from "react-dom";
import {
  getImgProps,
  type ImageProps as NextImageProps,
} from "next/dist/shared/lib/get-img-props";
import {
  prepareImageConfig,
  type ImageLoaderPropsWithConfig,
} from "next/dist/shared/lib/image-config";
import { COMPLETE_IMAGE_CONFIG } from "@shared/deployment/image-config";

export type ImageProps = Omit<
  NextImageProps,
  "onLoad" | "onLoadingComplete" | "loader" | "placeholder" | "blurDataURL" | "priority"
> & {
  onLoad?: () => void;
};

const RENDER_CONFIG = prepareImageConfig(COMPLETE_IMAGE_CONFIG);

function closestQuality(requested: number | undefined, qualities: readonly number[]): number {
  const target = requested ?? 75;
  return qualities.reduce(
    (closest, quality) => (Math.abs(quality - target) < Math.abs(closest - target) ? quality : closest),
    qualities[0] ?? target,
  );
}

const optimizerLoader = Object.assign(
  ({ config, src, width, quality }: ImageLoaderPropsWithConfig) =>
    `${config.path}?url=${encodeURIComponent(src)}&w=${width}&q=${closestQuality(quality, config.qualities ?? [])}`,
  { __next_img_default: true },
);

function useLoadReporter(onLoad: (() => void) | undefined) {
  const reportedSourceRef = useRef<string | null>(null);
  const report = useCallback(
    (image: HTMLImageElement) => {
      if (reportedSourceRef.current === image.currentSrc) return;
      reportedSourceRef.current = image.currentSrc;
      const decoded = "decode" in image ? image.decode() : Promise.resolve();
      void decoded
        .catch(() => undefined)
        .then(() => {
          if (image.isConnected) onLoad?.();
        });
    },
    [onLoad],
  );
  const attachRef = useCallback(
    (image: HTMLImageElement | null) => {
      if (image?.complete) report(image);
    },
    [report],
  );
  return { attachRef, report };
}

export function Image({ onLoad, alt, ...props }: ImageProps) {
  const { props: imageAttributes, meta } = getImgProps({ ...props, alt }, {
    defaultLoader: optimizerLoader,
    imgConf: RENDER_CONFIG,
  });
  const { attachRef, report } = useLoadReporter(onLoad);

  if (meta.preload && imageAttributes.src) {
    preload(imageAttributes.src, {
      as: "image",
      imageSrcSet: imageAttributes.srcSet,
      imageSizes: imageAttributes.sizes,
      fetchPriority: imageAttributes.fetchPriority,
      crossOrigin: imageAttributes.crossOrigin,
      referrerPolicy: imageAttributes.referrerPolicy,
    });
  }

  return (
    <img
      {...imageAttributes}
      alt={alt}
      ref={attachRef}
      data-nimg={meta.fill ? "fill" : "1"}
      onLoad={(event) => report(event.currentTarget)}
    />
  );
}
